/**
 * Utilitário de Terminologia de Contas
 * 
 * Centraliza o mapeamento de terminologia interna para exibição na UI.
 * - BOLSAO → Conta Master
 * - VIRTUAL → Conta Pagamento
 * - REAL → Conta Real
 */

// ============================================
// Tipos
// ============================================

export type AccountTypeInternal = 'BOLSAO' | 'VIRTUAL' | 'REAL';

export interface AccountTypeConfig {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  icon: string;
}

// ============================================
// Configurações por Tipo de Conta
// ============================================

export const ACCOUNT_TYPE_CONFIG: Record<AccountTypeInternal, AccountTypeConfig> = {
  BOLSAO: {
    label: 'Conta Master',
    description: 'Conta principal que centraliza os recursos do tenant. Recebe Pix e distribui para contas de pagamento.',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    icon: '🏦',
  },
  VIRTUAL: {
    label: 'Conta Pagamento',
    description: 'Conta vinculada a uma Conta Master. Permite receber e enviar Pix após aprovação de KYC.',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    icon: '💳',
  },
  REAL: {
    label: 'Conta Real',
    description: 'Conta com KYC completo gerenciado pelo provider. Possui autonomia total para operações.',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    icon: '🏛️',
  },
};

// ============================================
// Funções de Mapeamento
// ============================================

/**
 * Retorna o label de exibição para o tipo de conta
 */
export function getAccountTypeLabel(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal];
  return config?.label || type;
}

/**
 * Retorna a descrição do tipo de conta
 */
export function getAccountTypeDescription(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal];
  return config?.description || '';
}

/**
 * Retorna a cor do texto para o tipo de conta
 */
export function getAccountTypeColor(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal];
  return config?.color || 'text-surface-400';
}

/**
 * Retorna a cor de fundo para o tipo de conta
 */
export function getAccountTypeBgColor(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal];
  return config?.bgColor || 'bg-surface-500/20';
}

/**
 * Retorna o ícone para o tipo de conta
 */
export function getAccountTypeIcon(type: string): string {
  const config = ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal];
  return config?.icon || '📄';
}

/**
 * Retorna a configuração completa do tipo de conta
 */
export function getAccountTypeConfig(type: string): AccountTypeConfig | null {
  return ACCOUNT_TYPE_CONFIG[type as AccountTypeInternal] || null;
}

// ============================================
// Status de KYC
// ============================================

export type KycStatusType = 'NOT_STARTED' | 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED';

export interface KycStatusConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}

export const KYC_STATUS_CONFIG: Record<KycStatusType, KycStatusConfig> = {
  NOT_STARTED: {
    label: 'Não Iniciado',
    color: 'text-surface-400',
    bgColor: 'bg-surface-500/20',
    icon: '○',
  },
  PENDING: {
    label: 'Pendente',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    icon: '⏳',
  },
  IN_REVIEW: {
    label: 'Em Análise',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    icon: '🔍',
  },
  APPROVED: {
    label: 'Aprovado',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    icon: '✓',
  },
  REJECTED: {
    label: 'Rejeitado',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    icon: '✗',
  },
};

/**
 * Retorna a configuração de status de KYC
 */
export function getKycStatusConfig(status: string): KycStatusConfig {
  return KYC_STATUS_CONFIG[status as KycStatusType] || KYC_STATUS_CONFIG.NOT_STARTED;
}

/**
 * Retorna o label do status de KYC
 */
export function getKycStatusLabel(status: string): string {
  return getKycStatusConfig(status).label;
}

// ============================================
// Tipos de KYC
// ============================================

export const KYC_TYPE_LABELS: Record<string, string> = {
  ORIGIN: 'Origem',
  DESTINATION: 'Destino',
};

export function getKycTypeLabel(type: string): string {
  return KYC_TYPE_LABELS[type] || type;
}

// ============================================
// Helpers
// ============================================

/**
 * Verifica se o tipo de conta requer KYC
 * Apenas Conta Pagamento (VIRTUAL) requer KYC interno
 */
export function accountTypeRequiresKyc(type: string): boolean {
  return type === 'VIRTUAL';
}

/**
 * Verifica se a conta é uma Conta Pagamento
 */
export function isPaymentAccount(type: string): boolean {
  return type === 'VIRTUAL';
}

/**
 * Verifica se a conta é uma Conta Master
 */
export function isMasterAccount(type: string): boolean {
  return type === 'BOLSAO';
}
